import { SubscriptionInterval } from '@/store/types';
import { DBCustomerSchema, DBSubscriptionSchema, subscriptionName, subscriptionPlanPrice } from './billing-provider-utils';
import { SUBSCRIPTION_INTERVAL, COUPON_TYPE, PAYMENT_PROVIDERS } from '../store/shared';
import { dateMonthYearFormat } from './date-utils';

interface ConstructorOptions {
  subscriptionDocs?: DBSubscriptionSchema[];
  subscriptionId?: string;
}

// implemented from customer.model and subscription.model
class DBCustomer {
  provider: string;
  customerId: string;
  customerEmail?: string;
  customerName?: string;
  customerMetadata: any;
  customerSource: {
    sourceType: string;
    expiration: Date;
    funding: string;
    brand: string;
    country: string;
  };
  customerBalance: number;
  delinquent: boolean;
  customerCountry?: string;
  customerPostalCode?: string;
  customerState?: string;
  customerPreferredLocales?: string[];
  customerCurrency?: string;
  customerSubscription?: {
    id: string;
    isDelinquent: boolean;
    subscriptionStartDate: Date;
    subscriptionEndDate: Date;
    subscriptionCancelDate: Date;
    status: string;
    discount: any;
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    cancelAtPeriodEnd: boolean;
    items: any[];
    quantity: number;
    pauseCollection: any;
    trialStart: Date;
    trialEnd: Date;
    metadata: any;
  };
  customerPrice?: {
    id: string;
    amount: number;
    interval: string;
    intervalCount: number;
    type?: string;
  };
  customerBillingInterval?: SubscriptionInterval;
  customerBillingIntervalCount?: number;
  customerDiscount?: {
    id: string;
    coupon: {
      id: string;
      couponType: 'PERCENT' | 'AMOUNT';
      couponAmount: number;
      duration: null | number;
    };
    start?: Date;
    end?: Date;
  };
  subscriptionPriceString?: string;
  subscriptionNameString?: string;
  subscriptionEndedString?: string;

  // optional subscriptionId to specify
  constructor(customerDoc: DBCustomerSchema, { subscriptionDocs = [], subscriptionId }: ConstructorOptions = {}) {
    this.provider = customerDoc.paymentProvider || PAYMENT_PROVIDERS.STRIPE;

    this.customerId = customerDoc.customerId;
    this.customerEmail = customerDoc.email;
    this.customerName = customerDoc.name;
    this.customerMetadata = customerDoc.metadata;

    this.customerSource = customerDoc.source;
    this.customerBalance = customerDoc.balance;
    this.delinquent = customerDoc.delinquent;

    this.customerCountry = customerDoc.country;
    this.customerPostalCode = customerDoc.postalCode;
    this.customerState = customerDoc.state;
    this.customerPreferredLocales = customerDoc.preferredLocales;
    this.customerCurrency = customerDoc.currency;

    let subscription: DBSubscriptionSchema | undefined;
    if (subscriptionId) {
      subscription = subscriptionDocs.find((sub) => sub.subId === subscriptionId);
    }
    if (!subscription) {
      if (subscriptionDocs.length) {
        subscription = subscriptionDocs[0];
      }
    }
    if (!subscription) {
      return;
    }

    if (subscription.status === 'in_trial') {
      subscription.status = 'trialing';
    }
    if (subscription.status === 'future') {
      subscription.status = 'active';
    }
    if (subscription.status === 'cancelled') {
      subscription.status = 'canceled';
    }

    this.customerSubscription = {
      id: subscription.subId,
      isDelinquent: subscription.status === 'past_due',
      subscriptionStartDate: subscription.start_date || subscription.created,

      // Properties only exist in DBCustomer, not provider customer objects
      // TODO add to other provider classes if necessary
      subscriptionEndDate: new Date(subscription.ended_at),
      subscriptionCancelDate: new Date(subscription.canceled_at),

      status: subscription.status,
      discount: subscription.sub_discount,
      currentPeriodStart: new Date(subscription.current_period_start),
      currentPeriodEnd: new Date(subscription.current_period_end),

      // TODO Need to add to ingestion
      // Exists on Stripe customers as cancel_at_period_end when ingestion doesn't go through Mongoose
      // @ts-ignore
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd || subscription.cancel_at_period_end,

      items: subscription.subscription_items,
      quantity: subscription.quantity,
      pauseCollection: subscription.pause,
      trialStart: subscription.trial?.trialStart,
      trialEnd: subscription.trial?.trialEnd,
      metadata: subscription.metadata,
    };

    // PRICE/PLAN
    if (subscription.subscription_items?.length) {
      const price = subscription.subscription_items[0].price;
      this.customerPrice = {
        id: price.priceId,
        amount: price.amount,
        interval: price.interval,
        intervalCount: price.intervalCount,
        type: price.type,
      };
    } else if (subscription.plan) {
      const plan = subscription.plan;
      this.customerPrice = {
        id: plan.id,
        amount: plan.amount,
        interval: plan.interval,
        intervalCount: plan.interval_count || plan.intervalCount || 1,
      };
    }

    if (this.customerPrice && this.customerPrice.interval) {
      const intervalMap = {
        day: SUBSCRIPTION_INTERVAL.DAY,
        week: SUBSCRIPTION_INTERVAL.WEEK,
        month: SUBSCRIPTION_INTERVAL.MONTH,
        year: SUBSCRIPTION_INTERVAL.YEAR,
      };

      // @ts-ignore
      this.customerBillingInterval = intervalMap[this.customerPrice.interval] || this.customerPrice.interval;
      this.customerBillingIntervalCount = this.customerPrice.intervalCount;
    }

    // DISCOUNTS
    // WE DON'T HAVE THIS DATA FOR NON-STRIPE PROVIDERS
    function mapDbDiscount(dbDiscount: any) {
      const coupon = {
        id: dbDiscount.couponId,
        couponType: dbDiscount.discountMethod === 'percent' ? COUPON_TYPE.PERCENT : COUPON_TYPE.AMOUNT,
        couponAmount: dbDiscount.value,
        duration: null as null | number,
      };
      if (dbDiscount.duration === 'forever') {
        coupon.duration = null;
      } else if (dbDiscount.duration === 'once') {
        coupon.duration = 1;
      } else if (dbDiscount.duration === 'repeating') {
        coupon.duration = dbDiscount.durationInMonths;
      }

      return {
        id: dbDiscount.couponId,
        coupon,
        start: dbDiscount.start,
        end: dbDiscount.end,
      };
    }

    if (subscription.cust_discount) {
      // @ts-ignore
      this.customerDiscount = mapDbDiscount(subscription.cust_discount);
    }
    if (subscription.sub_discount) {
      this.customerSubscription.discount = mapDbDiscount(subscription.sub_discount);
    }

    // HELPERS
    this.subscriptionPriceString = subscriptionPlanPrice(subscription);
    this.subscriptionNameString = subscriptionName(subscription);
    this.subscriptionEndedString = '-';
    if (this.customerSubscription.subscriptionEndDate) {
      this.subscriptionEndedString = dateMonthYearFormat(this.customerSubscription.subscriptionEndDate);
    }
  }
}

export default DBCustomer;
